import {
  Dialog,
  DialogContent,
  Divider,
  FormControl,
  TextField,
  TextFieldProps,
  Typography,
} from "@mui/material";
import React, { FunctionComponent, useEffect, useState } from "react";
import { debounce, toColorInputValue } from "../../utils";
import ColorSinglePicker, {
  ColorSinglePickerProps,
  IconColor,
  PickEvent,
} from "./ColorSinglePicker";

type ColorPickerProps = Omit<ColorSinglePickerProps, "onChange"> & {
  onChange?: PickEvent;
  value?: string;
  modal?: boolean;
  /** Omit `onChange` — color changes use ColorPicker `onChange` (`PickEvent`). */
  textFieldProps?: Omit<TextFieldProps, "value" | "defaultValue" | "onChange">;
};

type InnerProp = Pick<ColorPickerProps, "colors"> & {
  onChange?: PickEvent;
  value?: string;
  textFieldProps?: ColorPickerProps["textFieldProps"];
};

const ColorPickerInner: FunctionComponent<InnerProp> = ({
  colors,
  onChange,
  value,
  textFieldProps,
}) => {
  const [_val, setVal] = useState<string>(() => toColorInputValue(value));

  useEffect(() => {
    setVal(toColorInputValue(value));
  }, [value]);

  return (
    <FormControl>
      <TextField
        {...textFieldProps}
        type="color"
        value={_val}
        onChange={(e) => {
          setVal(e.target.value);
          onChange?.(e as any);
        }}
        sx={{ mb: 2 }}
      />
      <ColorSinglePicker value={value} onPick={onChange} colors={colors} />
    </FormControl>
  );
};
const ColorPickerModal: FunctionComponent<InnerProp> = ({
  colors,
  onChange,
  value,
  textFieldProps,
}) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    return () => {
      setOpen(false);
    };
  }, []);

  return (
    <>
      <IconColor
        onClick={() => setOpen(true)}
        sx={{
          cursor: "pointer",
          transform: "scale(1.4)",
          color: value,
          "& svg": { width: 12, height: 12, color: "common.white" },
        }}
      />
      <Dialog
        open={open}
        onClose={() => {
          setOpen(false);
        }}
      >
        <DialogContent sx={{ padding: 4 }}>
          <Typography sx={{ fontSize: 18, fontWeight: "bold" }}>
            Pick Color
          </Typography>
          <Divider sx={{ my: 2 }} />
          <ColorPickerInner
            colors={colors}
            onChange={onChange}
            value={value}
            textFieldProps={textFieldProps}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default function ColorPicker({
  colors,
  onChange,
  value,
  modal,
  textFieldProps,
}: ColorPickerProps) {
  return (
    <>
      {modal ? (
        <ColorPickerModal
          colors={colors}
          onChange={onChange}
          value={value}
          textFieldProps={textFieldProps}
        />
      ) : (
        <ColorPickerInner
          onChange={onChange}
          value={value}
          colors={colors}
          textFieldProps={textFieldProps}
        />
      )}
    </>
  );
}
